/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Typography,
  FormControl,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  useTheme,
  useMediaQuery,
  InputAdornment,
  SelectChangeEvent,
  Skeleton,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import * as Sentry from "@sentry/react";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { RootState, useAppDispatch } from "@store/store";
import {
  ApiError,
  GarantiasVehicularInmobiliariaResponse,
  GuaranteeVehicleDetailConfig,
  PayloadRequestGarantias,
  PayloadRequestGarantiasVehicularInmobiliaria,
  VehicleStrutureCatalog,
} from "@interfaces/index";
import {
  useLazyGetVehiclesByYearAndMakeQuery,
  useLazyGetVehiclesByYearMakeAndModelQuery,
  useLazyGetVehiclesByYearQuery,
  useLazyGetVehiclesYearsQuery,
} from "@api/catalogs";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  useLazyGetGuaranteeTypeDetailConfigQuery,
  usePatchGuaranteesTypesMutation,
  usePostGuaranteesTypesMutation,
} from "@api/garantiasApi";
import { GuaranteeFormHandle } from "./guaranteeFormHandle";

interface VehicleProps {
  type_vehicle: string;
  year: string;
  brand: string;
  brand_value: string;
  model: string;
  model_value: string;
  version: string;
  version_value: string;
  km: string;
  estimated_value: string;
  id?: string;
}

interface FormGarantiaVehicularProps {
  guaranteesData: GarantiasVehicularInmobiliariaResponse[];
  guaranteeTypeId?: string;
  guaranteeLabel?: string;
  hideSubmitButton?: boolean;
  multiFlow?: boolean;
  onStatusChange?: (status: { isValid: boolean; isSubmitting: boolean }) => void;
  loading?: boolean;
}

const vehiculos_manual = [
  "Vans y Last mile delivery",
  "Tractocamiones",
  "Camionetas",
  "Camionetas Pickups",
  "Maquinaria amarilla",
  "Montacargas",
];

const FormGarantiaVehicularPymio = forwardRef<GuaranteeFormHandle, FormGarantiaVehicularProps>(
  (
    {
      guaranteesData,
      guaranteeTypeId,
      guaranteeLabel,
      hideSubmitButton,
      onStatusChange,
      loading = false,
    }: FormGarantiaVehicularProps,
    ref,
  ) => {
    const theme = useTheme();
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
    const dispatch = useAppDispatch();

    const application = useSelector((state: RootState) => state.register.application);

    const [triggerListVehicleYears] = useLazyGetVehiclesYearsQuery();
    const [triggerGetVehicleByYear] = useLazyGetVehiclesByYearQuery();
    const [triggerGetVehicleByYearAndBrand] = useLazyGetVehiclesByYearAndMakeQuery();
    const [triggerGetVehicleByYearBrandAndModel] = useLazyGetVehiclesByYearMakeAndModelQuery();
    const [postGuarantees] = usePostGuaranteesTypesMutation();
    const [patchGuarantees] = usePatchGuaranteesTypesMutation();
    const [getGuaranteeTypeDetailConfig] = useLazyGetGuaranteeTypeDetailConfigQuery();

    const emptyVehicle: VehicleProps = {
      type_vehicle: "",
      year: "",
      brand: "",
      brand_value: "",
      model: "",
      model_value: "",
      version: "",
      version_value: "",
      km: "",
      estimated_value: "",
      id: undefined,
    };

    const [brandsOptions, setBrandsOptions] = useState<Record<number, VehicleStrutureCatalog[]>>([]);
    const [modelsOptions, setModelsOptions] = useState<Record<number, VehicleStrutureCatalog[]>>([]);
    const [versionsOptions, setVersionsOptions] = useState<Record<number, VehicleStrutureCatalog[]>>([]);
    const [listYears, setListYears] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(loading);

    const [loadingBrand, setLoadingBrand] = useState<{ [index: number]: boolean }>({});
    const [loadingModel, setLoadingModel] = useState<{ [index: number]: boolean }>({});
    const [loadingVersion, setLoadingVersion] = useState<{ [index: number]: boolean }>({});
    const [guaranteeConfigCatalog, setGuaranteeConfigCatalog] = useState<GuaranteeVehicleDetailConfig[]>([]);

    const lastSubmitOkRef = useRef(false);

    type FormValues = {
      vehicles: VehicleProps[];
    };

    const isVehicleManualById = (typeVehicleId?: string) => {
      const selected = guaranteeConfigCatalog.find(
        (item) => item.guarantee_type_detail_id === String(typeVehicleId ?? ""),
      );
      const name = selected?.guarantee_type_detail.name ?? "";
      return vehiculos_manual.includes(name);
    };

    const formik = useFormik<FormValues>({
      initialValues: {
        vehicles: [{ ...emptyVehicle }],
      },
      validateOnMount: true,
      validationSchema: yup.object().shape({
        vehicles: yup.array().of(
          yup.object().shape({
            type_vehicle: yup.string().required("Campo requerido"),
            year: yup.string().required("Campo requerido"),
            brand: yup.string().required("Campo requerido"),
            model: yup.string().required("Campo requerido"),
            version: yup.string().required("Campo requerido"),
            km: yup.string().required("Campo requerido"),
            estimated_value: yup.string().when("type_vehicle", {
              is: (value?: string) => isVehicleManualById(value),
              then: (schema) => schema.required("Campo requerido"),
              otherwise: (schema) => schema.notRequired(),
            }),
          }),
        ),
      }),
      onSubmit: async (values, { setSubmitting }) => {
        try {
          lastSubmitOkRef.current = false;
          setSubmitting(true);
          setIsLoading(true);

          const guaranteesToPatch: Array<{
            guarantee_id: string;
            data: PayloadRequestGarantiasVehicularInmobiliaria;
          }> = [];
          const guaranteesToPost: PayloadRequestGarantiasVehicularInmobiliaria[] = [];

          values.vehicles.forEach((vehicle) => {
            const type_vehicle = guaranteeConfigCatalog.find((v) => v.guarantee_type_detail_id === vehicle.type_vehicle)
              ?.guarantee_type_detail.name;
            const cleanKm = Number(vehicle.km.replace(/,/g, "")) || 0;
            const cleanEstimatedValue = Number(String(vehicle.estimated_value ?? "").replace(/,/g, "")) || 0;
            const tracto = vehiculos_manual.includes(type_vehicle || "");

            const guaranteeItem: PayloadRequestGarantiasVehicularInmobiliaria = {
              loan_application_id: application.id,
              ...(guaranteeTypeId ? { guarantee_type_id: guaranteeTypeId } : {}),
              estimated_value: tracto ? cleanEstimatedValue : null,
              details: {
                vehicle_type: type_vehicle || "",
                guarantee_type_detail_id: tracto ? null : vehicle.type_vehicle,
                year: Number(vehicle.year) || 0,
                make: vehicle.brand_value || "",
                model: vehicle.model_value || "",
                version: vehicle.version_value || "",
                car_version_code: tracto ? null : String(vehicle.version),
                estimated_mileage: cleanKm,
              },
            };

            if (vehicle?.id) {
              guaranteesToPatch.push({ guarantee_id: vehicle.id, data: guaranteeItem });
            } else {
              guaranteesToPost.push(guaranteeItem);
            }
          });

          await Promise.all([
            ...guaranteesToPatch.map(({ guarantee_id, data }) =>
              patchGuarantees({ loan_application_id: application.id, guarantee_id, data }).unwrap(),
            ),
            guaranteesToPost.length > 0
              ? postGuarantees({
                  loan_application_id: application.id,
                  data: { guarantees_list: guaranteesToPost } as PayloadRequestGarantias,
                }).unwrap()
              : Promise.resolve(),
          ]);

          lastSubmitOkRef.current = true;
        } catch (error) {
          lastSubmitOkRef.current = false;
          const api_error = error as ApiError;

          Sentry.captureException(error, {
            extra: {
              component: "FormGarantiaVehicular-submit",
              values: formik.values,
            },
          });

          dispatch(setModalErrorVisible({ open: true, error: api_error }));
        } finally {
          setSubmitting(false);
          setIsLoading(false);
        }
      },
    });

    useEffect(() => {
      onStatusChange?.({ isValid: formik.isValid, isSubmitting: formik.isSubmitting });
    }, [formik.isValid, formik.isSubmitting, onStatusChange]);

    useImperativeHandle(
      ref,
      () => ({
        submit: async () => {
          lastSubmitOkRef.current = false;

          const errors = await formik.validateForm();
          if (Object.keys(errors).length > 0) {
            formik.setTouched({
              vehicles: formik.values.vehicles.map(() => ({
                type_vehicle: true,
                year: true,
                brand: true,
                model: true,
                version: true,
                km: true,
              })),
            });
            return false;
          }

          await formik.submitForm();
          return lastSubmitOkRef.current;
        },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [formik.values.vehicles],
    );

    const getFieldError = (fieldName: keyof VehicleProps, index: number): string | undefined => {
      const vehicleErrors = formik.errors.vehicles as Array<Partial<Record<keyof VehicleProps, string>>> | undefined;
      if (vehicleErrors && vehicleErrors[index] && vehicleErrors[index][fieldName]) {
        return vehicleErrors[index][fieldName];
      }
      return undefined;
    };

    const handleYearChange = async (e: SelectChangeEvent<string>, index: number) => {
      try {
        setIsLoading(true);
        setLoadingBrand((prev) => ({ ...prev, [index]: true }));
        setLoadingModel((prev) => ({ ...prev, [index]: true }));
        setLoadingVersion((prev) => ({ ...prev, [index]: true }));
        const { value } = e.target;
        formik.setFieldValue(`vehicles[${index}].year`, value);

        // Cargar las marcas
        const res = value ? await triggerGetVehicleByYear({ year: value }).unwrap() : [];

        // Resetear los valores de los select
        setBrandsOptions((prev) => ({ ...prev, [index]: res }));
        setModelsOptions((prev) => ({ ...prev, [index]: [] }));
        setVersionsOptions((prev) => ({ ...prev, [index]: [] }));

        // Resetear los valores de los inputs
        formik.setFieldValue(`vehicles[${index}].brand`, "");
        formik.setFieldValue(`vehicles[${index}].brand_value`, "");
        formik.setFieldValue(`vehicles[${index}].model`, "");
        formik.setFieldValue(`vehicles[${index}].model_value`, "");
        formik.setFieldValue(`vehicles[${index}].version`, "");
        formik.setFieldValue(`vehicles[${index}].version_value`, "");
      } catch (error) {
        console.error("Error al cargar marcas:", error);
        const api_error = error as ApiError;
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
        setLoadingBrand((prev) => ({ ...prev, [index]: false }));
        setLoadingModel((prev) => ({ ...prev, [index]: false }));
        setLoadingVersion((prev) => ({ ...prev, [index]: false }));
      }
    };

    const handleBrandChange = async (e: SelectChangeEvent<string>, index: number) => {
      try {
        setIsLoading(true);
        setLoadingModel((prev) => ({ ...prev, [index]: true }));
        setLoadingVersion((prev) => ({ ...prev, [index]: true }));
        const { value } = e.target;
        const { year } = formik.values.vehicles[index];

        // identificar la marca seleccionada
        const selectedBrand = brandsOptions[index]?.find((b) => b.code === value);
        formik.setFieldValue(`vehicles[${index}].brand`, value);
        formik.setFieldValue(`vehicles[${index}].brand_value`, selectedBrand?.name || "");

        // cargar los modelos
        const res = value ? await triggerGetVehicleByYearAndBrand({ year: year, make: value }).unwrap() : [];

        // resetear los valores de los select
        setModelsOptions((prev) => ({ ...prev, [index]: res }));
        setVersionsOptions((prev) => ({ ...prev, [index]: [] }));

        // resetear los valores de los inputs
        formik.setFieldValue(`vehicles[${index}].model`, "");
        formik.setFieldValue(`vehicles[${index}].model_value`, "");
        formik.setFieldValue(`vehicles[${index}].version`, "");
        formik.setFieldValue(`vehicles[${index}].version_value`, "");
      } catch (error) {
        const api_error = error as ApiError;
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
        console.error("Error al cargar marcas:", error);
      } finally {
        setIsLoading(false);
        setLoadingModel((prev) => ({ ...prev, [index]: false }));
        setLoadingVersion((prev) => ({ ...prev, [index]: false }));
      }
    };

    const handleModelChange = async (e: SelectChangeEvent<string>, index: number) => {
      try {
        setIsLoading(true);
        setLoadingVersion((prev) => ({ ...prev, [index]: true }));
        const { year, brand } = formik.values.vehicles[index];
        const { value } = e.target;

        // identificar el modelo seleccionado
        const selectedModel = modelsOptions[index]?.find((b) => b.code === value);
        formik.setFieldValue(`vehicles[${index}].model`, value);
        formik.setFieldValue(`vehicles[${index}].model_value`, selectedModel?.name || "");

        // cargar las versiones
        const res = await triggerGetVehicleByYearBrandAndModel({ year: year, make: brand, model: value }).unwrap();

        // resetear los valores de los select
        setVersionsOptions((prev) => ({ ...prev, [index]: res }));

        // resetear los valores de los inputs
        formik.setFieldValue(`vehicles[${index}].version`, "");
        formik.setFieldValue(`vehicles[${index}].version_value`, "");
      } catch (error) {
        console.error("Error al cargar el modelo:", error);
        const api_error = error as ApiError;
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
        setLoadingVersion((prev) => ({ ...prev, [index]: false }));
      }
    };

    const handleVersionChange = async (e: SelectChangeEvent<string>, index: number) => {
      const value = e.target.value;
      const selectedModel = versionsOptions[index]?.find((b) => b.code === value);
      formik.setFieldValue(`vehicles[${index}].version`, value);
      formik.setFieldValue(`vehicles[${index}].version_value`, selectedModel?.name || "");
    };

    useEffect(() => {
      const fetchInitialData = async () => {
        try {
          setIsLoading(true);

          const response_years_list = await triggerListVehicleYears().unwrap();
          const response_guarantee_config = await getGuaranteeTypeDetailConfig({
            guarantee_type_id: guaranteeTypeId ?? "",
            taxcode: application.type,
          }).unwrap();

          setListYears(response_years_list);
          setGuaranteeConfigCatalog(response_guarantee_config);

          const response_garantia_data = guaranteesData;

          if (response_garantia_data.length > 0) {
            const mappedVehicles = await Promise.all(
              
              response_garantia_data.map(async (item, index) => {
                const vehicle_type_id = response_guarantee_config.find(
                  (v) => v.guarantee_type_detail.name === item.details?.vehicle_type,
                )?.guarantee_type_detail_id;
                const type = String(item.details?.vehicle_type || "");
                const tracto = vehiculos_manual.includes(type);
                const isMaquinariaAmarilla = type === "Maquinaria amarilla" || type === "Montacargas";
                const year = item.details?.year?.toString() || "";
                const km = isMaquinariaAmarilla
                  ? String(item.details?.estimated_mileage ?? "")
                  : item.details?.estimated_mileage?.toLocaleString("en-US") || "";

                const estimatedValue = Number(item.estimated_value ?? 0);
                const estimatedValueFormatted = estimatedValue > 0 ? estimatedValue.toLocaleString("en-US") : "";

                console.log(vehicle_type_id);

                if (tracto) {
                  return {
                    type_vehicle: vehicle_type_id || "",
                    year,
                    brand_value: item.details?.make || "",
                    brand: item.details?.make || "",
                    model_value: item.details?.model || "",
                    model: item.details?.model || "",
                    version: item.details?.version || "",
                    version_value: item.details?.version || "",
                    km,
                    estimated_value: estimatedValueFormatted,
                    id: String(item.id),
                  };
                }

                let brandCatalog: VehicleStrutureCatalog[] = [];
                let modelCatalog: VehicleStrutureCatalog[] = [];
                let versionCatalog: VehicleStrutureCatalog[] = [];

                let selectedBrandCode = "";
                let selectedModelCode = "";

                if (year) {
                  brandCatalog = await triggerGetVehicleByYear({ year }).unwrap();
                  setBrandsOptions((prev) => ({ ...prev, [index]: brandCatalog }));

                  const selectedBrand = brandCatalog.find((b) => b.name === item.details?.make);
                  selectedBrandCode = selectedBrand?.code || "";

                  if (selectedBrandCode) {
                    modelCatalog = await triggerGetVehicleByYearAndBrand({
                      year,
                      make: selectedBrandCode,
                    }).unwrap();
                    setModelsOptions((prev) => ({ ...prev, [index]: modelCatalog }));

                    const selectedModel = modelCatalog.find((m) => m.name === item.details?.model);
                    selectedModelCode = selectedModel?.code || "";

                    if (selectedModelCode) {
                      versionCatalog = await triggerGetVehicleByYearBrandAndModel({
                        year,
                        make: selectedBrandCode,
                        model: selectedModelCode,
                      }).unwrap();
                      setVersionsOptions((prev) => ({ ...prev, [index]: versionCatalog }));
                    }
                  }
                }

                return {
                  type_vehicle: vehicle_type_id || "",
                  year,
                  brand_value: item.details?.make || "",
                  brand: selectedBrandCode,
                  model_value: item.details?.model || "",
                  model: selectedModelCode,
                  version: item.details?.car_version_code || "",
                  version_value: item.details?.version || "",
                  km,
                  estimated_value: estimatedValueFormatted,
                  id: item.id,
                };
              }),
            );

            setTimeout(() => {
              formik.setFieldValue("vehicles", mappedVehicles);
            }, 500);
          }
        } catch (error) {
          const api_error = error as ApiError;
          Sentry.captureException(error, {
            extra: {
              component: "FormGarantiaVehicular-initial-data",
              values: formik.values,
            },
          });
          dispatch(setModalErrorVisible({ open: true, error: api_error }));
        } finally {
          setIsLoading(false);
        }
      };

      fetchInitialData();
    }, [guaranteesData]);

    const handleChangeEstimatedValue = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
      const rawValue = e.target.value.replace(/[^0-9]/g, "");
      const numericValue = Number(rawValue) || 0;
      const formattedValue = rawValue ? numericValue.toLocaleString("en-US") : "";
      formik.setFieldValue(`vehicles[${index}].estimated_value`, formattedValue);
    };

    return (
      <Box>
        <form onSubmit={formik.handleSubmit} noValidate>
          <Typography
            mt={isMobileOrTablet ? 2 : 3}
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.dark}
            textAlign={"center"}
          >
            Detalle de Garantía {guaranteeLabel}
          </Typography>
          {formik.values.vehicles.map((vehicle: VehicleProps, index: number) => {
            const type_vehicle = guaranteeConfigCatalog.find(
              (v) => v.guarantee_type_detail_id === vehicle.type_vehicle,
            );
            const isManual = vehiculos_manual.includes(type_vehicle?.guarantee_type_detail.name || "");
            const isMaquinariaAmarilla =
              type_vehicle?.guarantee_type_detail.name === "Maquinaria amarilla" ||
              type_vehicle?.guarantee_type_detail.name === "Montacargas";
            return (
              <Box key={index} mt={index > 0 ? 4 : 0}>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel id={`type_vehicle-${index}`} required>
                    Tipo de Vehículo
                  </InputLabel>

                  <Select
                    labelId={`type_vehicle-${index}`}
                    id={`type_vehicle-${index}`}
                    name={`vehicles[${index}].type_vehicle`}
                    fullWidth
                    required
                    label="Tipo de Vehículo"
                    value={vehicle.type_vehicle}
                    onBlur={formik.handleBlur}
                    onChange={(e) => {
                      const value = e.target.value;
                      const wasManual = isVehicleManualById(vehicle.type_vehicle);
                      const willBeManual = isVehicleManualById(String(value));
                      formik.setFieldValue(`vehicles[${index}].type_vehicle`, value);

                      if (wasManual !== willBeManual) {
                        formik.setFieldValue(`vehicles[${index}].year`, "");
                        formik.setFieldValue(`vehicles[${index}].brand`, "");
                        formik.setFieldValue(`vehicles[${index}].brand_value`, "");
                        formik.setFieldValue(`vehicles[${index}].model`, "");
                        formik.setFieldValue(`vehicles[${index}].model_value`, "");
                        formik.setFieldValue(`vehicles[${index}].version`, "");
                        formik.setFieldValue(`vehicles[${index}].version_value`, "");
                        formik.setFieldValue(`vehicles[${index}].km`, "");
                        formik.setFieldValue(`vehicles[${index}].estimated_value`, "");
                      }
                    }}
                    error={Boolean(
                      formik.touched.vehicles?.[index]?.type_vehicle && getFieldError("type_vehicle", index),
                    )}
                    disabled={isLoading}
                  >
                    <MenuItem value={""}>Selecciona</MenuItem>
                    {guaranteeConfigCatalog.map((vehicle) => (
                      <MenuItem key={vehicle.id} value={vehicle.guarantee_type_detail_id}>
                        {vehicle.guarantee_type_detail.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.vehicles?.[index]?.type_vehicle && getFieldError("type_vehicle", index) && (
                    <FormHelperText>{getFieldError("type_vehicle", index)}</FormHelperText>
                  )}
                </FormControl>

                {isManual ? (
                  <TextField
                    fullWidth
                    label="Año"
                    name={`vehicles[${index}].year`}
                    value={vehicle.year}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d{0,4}$/.test(value)) {
                        formik.handleChange(e);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    id={`year-${index}`}
                    margin="normal"
                    required
                    autoComplete="off"
                    error={Boolean(formik.touched.vehicles?.[index]?.year && getFieldError("year", index))}
                    helperText={formik.touched.vehicles?.[index]?.year && getFieldError("year", index)}
                    inputProps={{ maxLength: 4 }}
                  />
                ) : (
                  <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel id={`year-${index}`} required>
                      Año
                    </InputLabel>

                    <Select
                      labelId={`year-${index}`}
                      id={`year-${index}`}
                      name={`vehicles[${index}].year`}
                      fullWidth
                      required
                      label="Año"
                      value={vehicle.year}
                      onBlur={formik.handleBlur}
                      onChange={(e) => handleYearChange(e, index)}
                      error={Boolean(formik.touched.vehicles?.[index]?.year && getFieldError("year", index))}
                      disabled={isLoading}
                    >
                      <MenuItem value={""}>Selecciona</MenuItem>
                      {listYears?.map((year, index) => (
                        <MenuItem key={index} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.vehicles?.[index]?.year && getFieldError("year", index) && (
                      <FormHelperText>{getFieldError("year", index)}</FormHelperText>
                    )}
                  </FormControl>
                )}

                {isManual ? (
                  <TextField
                    fullWidth
                    label="Marca"
                    name={`vehicles[${index}].brand`}
                    value={vehicle.brand}
                    onChange={(e) => {
                      const value = e.target.value;
                      formik.setFieldValue(`vehicles[${index}].brand_value`, value);
                      formik.setFieldValue(`vehicles[${index}].brand`, value);
                    }}
                    onBlur={formik.handleBlur}
                    id={`brand-${index}`}
                    margin="normal"
                    required
                    autoComplete="off"
                    error={Boolean(formik.touched.vehicles?.[index]?.brand && getFieldError("brand", index))}
                    helperText={formik.touched.vehicles?.[index]?.brand && getFieldError("brand", index)}
                  />
                ) : (
                  <>
                    {loadingBrand[index] ? (
                      <Skeleton variant="rectangular" height={56} sx={{ mt: 2, borderRadius: 1 }} />
                    ) : (
                      <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel id={`brand-${index}`} required>
                          Marca
                        </InputLabel>

                        <Select
                          labelId={`brand-${index}`}
                          id={`brand-${index}`}
                          name={`vehicles[${index}].brand`}
                          fullWidth
                          required
                          label="Marca"
                          value={vehicle.brand}
                          onBlur={formik.handleBlur}
                          onChange={(e) => {
                            handleBrandChange(e, index);
                          }}
                          error={Boolean(formik.touched.vehicles?.[index]?.brand && getFieldError("brand", index))}
                          disabled={isLoading}
                        >
                          <MenuItem value={""}>Selecciona</MenuItem>
                          {brandsOptions[index]?.map((brand) => (
                            <MenuItem key={brand.code} value={brand.code}>
                              {brand.name}
                            </MenuItem>
                          ))}
                        </Select>

                        {formik.touched.vehicles?.[index]?.brand && getFieldError("brand", index) && (
                          <FormHelperText>{getFieldError("brand", index)}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  </>
                )}

                {isManual ? (
                  <TextField
                    fullWidth
                    label="Modelo"
                    name={`vehicles[${index}].model`}
                    value={vehicle.model}
                    onChange={(e) => {
                      const value = e.target.value;
                      formik.setFieldValue(`vehicles[${index}].model_value`, value);
                      formik.setFieldValue(`vehicles[${index}].model`, value);
                    }}
                    onBlur={formik.handleBlur}
                    id={`model-${index}`}
                    margin="normal"
                    required
                    autoComplete="off"
                    error={Boolean(formik.touched.vehicles?.[index]?.model && getFieldError("model", index))}
                    helperText={formik.touched.vehicles?.[index]?.model && getFieldError("model", index)}
                  />
                ) : (
                  <>
                    {loadingModel[index] ? (
                      <Skeleton variant="rectangular" height={56} sx={{ mt: 2, borderRadius: 1 }} />
                    ) : (
                      <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel id={`model-${index}`} required>
                          Modelo
                        </InputLabel>

                        <Select
                          labelId={`model-${index}`}
                          id={`model-${index}`}
                          name={`vehicles[${index}].model`}
                          fullWidth
                          required
                          label="Modelo"
                          value={vehicle.model}
                          onBlur={formik.handleBlur}
                          onChange={(e) => {
                            handleModelChange(e, index);
                          }}
                          error={Boolean(formik.touched.vehicles?.[index]?.model && getFieldError("model", index))}
                          disabled={isLoading}
                        >
                          <MenuItem value={""}>Selecciona</MenuItem>
                          {modelsOptions[index]?.map((model) => (
                            <MenuItem key={model.code} value={model.code}>
                              {model.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {formik.touched.vehicles?.[index]?.model && getFieldError("model", index) && (
                          <FormHelperText>{getFieldError("model", index)}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  </>
                )}

                {isManual ? (
                  <TextField
                    fullWidth
                    label="Versión"
                    name={`vehicles[${index}].version`}
                    value={vehicle.version}
                    onChange={(e) => {
                      const value = e.target.value;
                      formik.setFieldValue(`vehicles[${index}].version_value`, value);
                      formik.setFieldValue(`vehicles[${index}].version`, value);
                    }}
                    onBlur={formik.handleBlur}
                    id={`version-${index}`}
                    margin="normal"
                    required
                    autoComplete="off"
                    error={Boolean(formik.touched.vehicles?.[index]?.version && getFieldError("version", index))}
                    helperText={formik.touched.vehicles?.[index]?.version && getFieldError("version", index)}
                  />
                ) : (
                  <>
                    {loadingVersion[index] ? (
                      <Skeleton variant="rectangular" height={56} sx={{ mt: 2, borderRadius: 1 }} />
                    ) : (
                      <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel id={`version-${index}`} required>
                          Versión
                        </InputLabel>

                        <Select
                          labelId={`version-${index}`}
                          id={`version-${index}`}
                          name={`vehicles[${index}].version`}
                          fullWidth
                          required
                          label="Versión"
                          value={vehicle.version}
                          onBlur={formik.handleBlur}
                          onChange={(e) => {
                            handleVersionChange(e, index);
                          }}
                          error={Boolean(formik.touched.vehicles?.[index]?.version && getFieldError("version", index))}
                          disabled={isLoading}
                        >
                          <MenuItem value={""}>Selecciona</MenuItem>
                          {versionsOptions[index]?.map((version) => (
                            <MenuItem key={version.code} value={version.code}>
                              {version.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {formik.touched.vehicles?.[index]?.version && getFieldError("version", index) && (
                          <FormHelperText>{getFieldError("version", index)}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  </>
                )}

                <FormControl fullWidth>
                  <TextField
                    id={`km-${index}`}
                    name={`vehicles[${index}].km`}
                    label={isMaquinariaAmarilla ? "Horas de uso/Km" : "Km"}
                    margin="normal"
                    required
                    fullWidth
                    autoComplete="off"
                    onBlur={formik.handleBlur}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, "");
                      if (isMaquinariaAmarilla) {
                        formik.setFieldValue(`vehicles[${index}].km`, rawValue);
                        return;
                      }

                      const formattedValue = rawValue ? Number(rawValue).toLocaleString("en-US") : "";
                      formik.setFieldValue(`vehicles[${index}].km`, formattedValue);
                    }}
                    error={Boolean(formik.touched.vehicles?.[index]?.km && getFieldError("km", index))}
                    helperText={formik.touched.vehicles?.[index]?.km && getFieldError("km", index)}
                    value={vehicle.km}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">{isMaquinariaAmarilla ? "Hrs/Km" : "Km"}</InputAdornment>
                      ),
                    }}
                    inputProps={{
                      maxLength: 11,
                      inputMode: "numeric",
                    }}
                    disabled={isLoading}
                  />
                </FormControl>

                {isManual && (
                  <FormControl fullWidth>
                    <TextField
                      id={`estimated_value-${index}`}
                      name={`vehicles[${index}].estimated_value`}
                      label="Valor Estimado"
                      margin="normal"
                      required={isManual}
                      fullWidth
                      autoComplete="off"
                      onBlur={formik.handleBlur}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeEstimatedValue(e, index)}
                      inputProps={{ maxLength: 20, inputMode: "numeric" }}
                      value={vehicle.estimated_value}
                      disabled={isLoading}
                      error={Boolean(
                        formik.touched.vehicles?.[index]?.estimated_value && getFieldError("estimated_value", index),
                      )}
                      helperText={
                        formik.touched.vehicles?.[index]?.estimated_value ? getFieldError("estimated_value", index) : ""
                      }
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                  </FormControl>
                )}
              </Box>
            );
          })}

          {!hideSubmitButton && (
            <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
              <Button
                variant="default"
                type="submit"
                color="primary"
                disabled={!formik.dirty || !formik.isValid || formik.isSubmitting}
              >
                Continuar
              </Button>
            </Box>
          )}
        </form>
      </Box>
    );
  },
);

FormGarantiaVehicularPymio.displayName = "FormGarantiaVehicularPymio";

export default FormGarantiaVehicularPymio;
